import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UserPrice, UserPriceRequest, UserSettings, UserSettingsRequest } from './api'
import {
  deleteUserPriceOverride,
  deleteUserSettingsOverride,
  getUserLimit,
  getUserPrice,
  updateUserPrice,
  updateUserSettings,
} from './endpoints'
import { mapPersonLimitToUserSettings, mapUserPriceDtoToUserPrice } from './mappers'

const QueryKey = {
  UserSettings: (customerId: string) => ['user-settings', customerId] as const,
  UserPrice: (customerId: string) => ['user-price', customerId] as const,
  UpdateUserSettings: (customerId: string) => ['update-user-settings', customerId] as const,
  UpdateUserPrice: (customerId: string) => ['update-user-price', customerId] as const,
}

export const invalidateUserSettings = ({ queryClient, customerId }: { queryClient: QueryClient; customerId: string }) => {
  queryClient.invalidateQueries({ queryKey: QueryKey.UserSettings(customerId) })
  queryClient.invalidateQueries({ queryKey: QueryKey.UserPrice(customerId) })
}

export const useUserLimit = (customerId: string) => {
  return useQuery({
    queryKey: QueryKey.UserSettings(customerId),
    queryFn: () => getUserLimit(customerId),
  })
}

export const useUserPrice = (customerId: string) => {
  return useQuery({
    queryKey: QueryKey.UserPrice(customerId),
    queryFn: () => getUserPrice(customerId),
  })
}

export const useUpdateUserSettings = (customerId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: QueryKey.UpdateUserSettings(customerId),
    mutationFn: (settings: UserSettingsRequest) => updateUserSettings(customerId, settings),
    onSuccess: ({ data }) => {
      queryClient.setQueryData(QueryKey.UserSettings(customerId), mapPersonLimitToUserSettings(data))
    },
  })
}

export const useUpdateUserPrice = (customerId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: QueryKey.UpdateUserPrice(customerId),
    mutationFn: (price: UserPriceRequest) => updateUserPrice(customerId, price),
    onSuccess: ({ data }) => {
      queryClient.setQueryData(QueryKey.UserPrice(customerId), mapUserPriceDtoToUserPrice(data))
    },
  })
}

export const useDeleteUserSettings = (customerId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => deleteUserSettingsOverride(customerId),
    onSuccess: (data) => {
      queryClient.setQueryData(QueryKey.UserSettings(customerId), data)
    },
  })
}

export const useDeleteUserPrice = (customerId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => deleteUserPriceOverride(customerId),
    onSuccess: (data) => {
      queryClient.setQueryData(QueryKey.UserPrice(customerId), data)
    },
  })
}
